import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  signal,
  effect,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DrawerModule } from 'primeng/drawer';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { BusinessTermDrawerService } from '../../services/business-term-drawer.service';
import { BusinessGlossaryService } from 'src/app/features/business-glossary-management/services/business-glossary.service';
import { Term } from 'src/app/features/business-glossary-management/models/business-glossary.models';
import { ChipModule } from 'primeng/chip';
import { MatSpinnerOverlayComponent } from '../mat-spinner-overlay/mat-spinner-overlay.component';

@Component({
  selector: 'elx-business-term-drawer',
  standalone: true,
  imports: [
    CommonModule,
    DrawerModule,
    TabsModule,
    ButtonModule,
    TooltipModule,
    TagModule,
    ChipModule,
    DatePipe,
    MatSpinnerOverlayComponent,
  ],
  templateUrl: './business-term-drawer.component.html',
  styleUrls: ['./business-term-drawer.component.scss'],
})
export class BusinessTermDrawerComponent implements OnInit {
  private readonly _drawerService = inject(BusinessTermDrawerService);
  private readonly _glossaryService = inject(BusinessGlossaryService);
  private readonly _destroyRef = inject(DestroyRef);

  // Service state signals
  isOpen = this._drawerService.isOpen;
  termId = this._drawerService.termId;
  termData = this._drawerService.termData;
  isLoading = this._drawerService.isLoading;
  error = this._drawerService.error;

  // Local state
  // Default to first tab (Guideline 1: predictable UI)
  activeTab = signal<string>('overview');

  constructor() {
    // Watch for ID changes and fetch data
    effect(() => {
      const id = this.termId();
      if (id !== null) {
        // Reset to first tab whenever drawer opens on a new term
        this.activeTab.set('overview');
        this.loadTerm(id);
      }
    });
  }

  ngOnInit(): void {
    // Component initialization
  }

  private loadTerm(id: string): void {
    this._drawerService.setLoading(true);
    this._drawerService.setError(null);

    this._glossaryService
      .getTermById(id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          const term = response.payload;
          this._drawerService.setTermData(term);

          // If categoryName is missing but categoryId exists, fetch category details
          if (!term.categoryName && term.categoryId) {
            this._glossaryService
              .getById(term.categoryId)
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe({
                next: (catResponse) => {
                  const categoryName = catResponse.payload?.name;
                  if (categoryName) {
                    this._drawerService.setTermData({
                      ...term,
                      categoryName: categoryName,
                    });
                  }
                },
                error: (err) => {
                                  },
              });
          }
        },
        error: (error) => {
                    this._drawerService.setError(
            'Failed to load term details. Please try again.',
          );
        },
      });
  }

  onClose(): void {
    this._drawerService.close();
  }

  onRetry(): void {
    const id = this.termId();
    if (id !== null) {
      this.loadTerm(id);
    }
  }

  getOwnerName(owner: any): string {
    if (!owner) return 'Unknown';
    if (owner.firstName && owner.lastName) {
      return `${owner.firstName} ${owner.lastName}`;
    }
    return owner.username || owner.email || 'Unknown';
  }

  getStatusSeverity(
    status: string,
  ): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
    const statusMap: Record<
      string,
      'success' | 'warning' | 'danger' | 'info' | 'secondary'
    > = {
      APPROVED: 'success',
      DRAFT: 'warning',
      DEPRECATED: 'danger',
      REVIEW: 'info',
    };
    return statusMap[status?.toUpperCase()] || 'info';
  }
}
